import {makeObservable, observable, action} from 'mobx';


export type ScreenSize = 'mobile' | 'tablet' | 'desktop';


export const DESKTOP_MIN_WIDTH = 1250;
export const TABLET_MIN_WIDTH = 480;


export class ScreenSizeProvider {
    view: ScreenSize;
    size: ResizeObserverSize;
    isThrottling: boolean = false;

    constructor() {
        // Initialize fields before making them observable
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
            this.size = {
                inlineSize: window.innerWidth,
                blockSize: window.innerHeight,
            };
        } else {
            // Default values for server-side rendering or tests
            this.size = {
                inlineSize: 1920, // Default desktop width
                blockSize: 1080,  // Default desktop height
            };
        }
        
        this.view = this.getView(this.size);

        // Now make them observable after initialization
        makeObservable(this, {
            size: observable,
            view: observable,
            setSize: action,
            setView: action,
        });

        // Set up event listener only in browser
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.throttledResizeHandler);
        }
        this.setDynamicHeight();
    }

    // Throttled resize handler to limit frequency of resize events
    throttledResizeHandler = () => {
        if (typeof window === 'undefined') return;
        
        if (!this.isThrottling) {
            this.isThrottling = true;
            requestAnimationFrame(() => {
                this.setDynamicHeight();
                this.isThrottling = false;
            });
        }
    };

    // Clean up event listener when this provider is no longer needed
    destroy = () => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.throttledResizeHandler);
        }
    };

    setDynamicHeight = () => {
        if (typeof window === 'undefined') return;
        
        try {
            const newSize = {
                inlineSize: window.innerWidth,
                blockSize: window.innerHeight,
            };

            // Only update if the size has actually changed
            if (newSize.inlineSize !== this.size.inlineSize || newSize.blockSize !== this.size.blockSize) {
                this.setSize(newSize);
                this.setView(this.getView(newSize));
            }

            // Update the CSS custom property for --vh
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        } catch (error) {
            // Handle potential errors
        }
    }

    setSize = (size: ResizeObserverSize) => {
        this.size = size;
    }

    setView = (view: ScreenSize) => {
        this.view = view;
    }

    getView = (size: ResizeObserverSize): ScreenSize => {
        if (size.inlineSize >= DESKTOP_MIN_WIDTH) {
            return 'desktop';
        } else if (size.inlineSize > TABLET_MIN_WIDTH) {
            return 'tablet';
        }
        return 'mobile';
    }
}

export const screenSizeProvider = new ScreenSizeProvider();
